import { useState } from 'react';
import { X } from 'lucide-react';
import { useFunding, type CommitmentWithDetails } from '../../hooks/useFunding';

interface Props {
  commitment: CommitmentWithDetails;
  onClose: () => void;
}

export function FundingDisbursementModal({ commitment, onClose }: Props) {
  const { createDisbursement } = useFunding();
  const [formData, setFormData] = useState({
    amount: '',
    paid_date: new Date().toISOString().split('T')[0],
    reference: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      await createDisbursement({
        commitment_id: commitment.id,
        amount: parseFloat(formData.amount),
        paid_date: formData.paid_date,
        reference: formData.reference || undefined,
        notes: formData.notes || undefined
      });
      onClose();
    } catch (error) {
      console.error('Error saving disbursement:', error);
    } finally {
      setSaving(false);
    }
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">
            Nouveau versement
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {/* Commitment Info */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Engagement</p>
            <p className="font-medium text-gray-900">{commitment.source?.name}</p>
            <p className="text-sm text-gray-600 mt-1">{commitment.title}</p>
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-600">Promis: </span>
                <span className="font-medium">{formatAmount(parseFloat(commitment.commitment_amount.toString()))} €</span>
              </div>
              <div>
                <span className="text-gray-600">Verse: </span>
                <span className="font-medium text-emerald-600">{formatAmount(commitment.paid_total)} €</span>
              </div>
              <div className="col-span-2">
                <span className="text-gray-600">Reste: </span>
                <span className="font-medium text-orange-600">{formatAmount(commitment.remaining)} €</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Montant verse *
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  required
                  min="0"
                  step="0.01"
                  max={commitment.remaining}
                />
                <span className="absolute right-3 top-2 text-gray-500">€</span>
              </div>
              {parseFloat(formData.amount) > commitment.remaining && (
                <p className="text-xs text-orange-600 mt-1">
                  Attention: ce montant depasse le reste a verser
                </p>
              )}
            </div>

            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date de versement *
              </label>
              <input
                type="date"
                value={formData.paid_date}
                onChange={(e) => setFormData({ ...formData, paid_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                required
              />
            </div>

            {/* Reference */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reference (optionnel)
              </label>
              <input
                type="text"
                value={formData.reference}
                onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                placeholder="Ex: Numero de virement, piece comptable"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-50"
              >
                {saving ? 'Enregistrement...' : 'Ajouter le versement'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
